export type ContractVisualState = 'not_sent' | 'sending' | 'waiting_signature' | 'signed' | 'error';

export type ContractStatus = 'not_sent' | 'sending' | 'waiting' | 'signed' | 'error';

export interface ContractViewData {
  status: ContractStatus;
  signerEmail: string;
  signerName: string;
  sentAt: string | null;
  signedAt: string | null;
  contractVersionId: string;
  type: string;
  version: number;
}

export const CONTRACT_RESEND_COOLDOWN_SECONDS = 60;

export function secondsUntilContractResend(sentAt: string | null, now = Date.now()): number {
  if (!sentAt) return CONTRACT_RESEND_COOLDOWN_SECONDS;
  const elapsed = Math.floor((now - new Date(sentAt).getTime()) / 1_000);
  return Math.max(0, CONTRACT_RESEND_COOLDOWN_SECONDS - elapsed);
}

export function shouldPollContractSignatures(
  statuses: readonly ContractStatus[],
  pendingCount: number,
): boolean {
  return (
    pendingCount > 0 || statuses.some((status) => status === 'waiting' || status === 'sending')
  );
}

function isWaiting(status: ContractStatus | undefined | null): boolean {
  return status === 'waiting';
}

function isSigned(status: ContractStatus | undefined | null): boolean {
  return status === 'signed';
}

function isTerminalError(status: ContractStatus | undefined | null): boolean {
  return status === 'error';
}

export function deriveContractVisualState(
  contract: ContractViewData | null,
  isSending: boolean,
): ContractVisualState {
  if (!contract) {
    return isSending ? 'sending' : 'not_sent';
  }
  if (isSigned(contract.status)) {
    return 'signed';
  }
  if (isTerminalError(contract.status)) {
    return 'error';
  }
  if (isWaiting(contract.status)) {
    return 'waiting_signature';
  }
  if (contract.status === 'sending') return 'sending';
  if (isSending) return 'sending';
  return 'not_sent';
}
